/**
 * GOOGLE MAP COMPONENT
 */

import React, { useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';

const center = { lat: 38.0406, lng: -84.5037 }; // Lexington, KY

function CopperRoadMap({ venues, checkedInVenueIds, onVenueSelect }) {
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
    });

    const [map, setMap] = useState(null);
    const [activeMarker, setActiveMarker] = useState(null);

    // Map Categories to Colors — matching design tokens
    const categoryColors = {
        'Distillery': '#B87333', // Copper
        'Bar': '#8E44AD',        // Purple (#8E44AD from mockup colors for bar)
        'Restaurant': '#E67E22', // Orange/Bourbon
        'Attraction': '#27AE60', // Green
        'Lodging': '#1C6EA4'     // Blue
    };

    const getPinColor = (category) => {
        const cat = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
        return categoryColors[cat] || '#B87333';
    };

    const createPinSVG = (color, isCheckedIn) => {
        // A simple custom SVG pin string encoded for data URI
        const fill = isCheckedIn ? color : '#ffffff';
        const stroke = color;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
            <path d="M15 0C6.716 0 0 6.716 0 15c0 10 15 25 15 25s15-15 15-25C30 6.716 23.284 0 15 0z" fill="${fill}" stroke="${stroke}" stroke-width="3"/>
            <circle cx="15" cy="15" r="5" fill="${isCheckedIn ? '#fff' : stroke}"/>
        </svg>`;
        return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    };

    const onLoad = React.useCallback(function callback(m) {
        setMap(m);
    }, []);

    React.useEffect(() => {
        if (map && venues.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            venues.forEach(v => bounds.extend({ lat: Number(v.latitude), lng: Number(v.longitude) }));
            map.fitBounds(bounds);
        }
    }, [map, venues]);

    const onUnmount = React.useCallback(function callback(m) {
        setMap(null);
    }, []);

    if (!isLoaded) return <div style={{ height: '400px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🥃 Loading Map... (Requires API Key)</div>;

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Category Legend */}
            <div style={legendStyles.container}>
                <div style={legendStyles.title}>Legend</div>
                {Object.entries(categoryColors).map(([cat, color]) => (
                    <div key={cat} style={legendStyles.item}>
                        <div style={{ ...legendStyles.swatch, backgroundColor: color }} />
                        {cat}
                    </div>
                ))}
            </div>

            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={center}
                zoom={10}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                    styles: mapStyles,
                    disableDefaultUI: true,
                    zoomControl: true,
                }}
            >
                {venues.map(v => {
                    const checkedIn = checkedInVenueIds.includes(v.id);
                    return (
                        <Marker
                            key={v.id}
                            position={{ lat: Number(v.latitude), lng: Number(v.longitude) }}
                            icon={{
                                url: createPinSVG(getPinColor(v.category), checkedIn),
                                anchor: new window.google.maps.Point(15, 40)
                            }}
                            onClick={() => setActiveMarker(v)}
                            title={v.name}
                        />
                    );
                })}

                {/* InfoWindow */}
                {activeMarker && (
                    <InfoWindow
                        position={{ lat: Number(activeMarker.latitude), lng: Number(activeMarker.longitude) }}
                        onCloseClick={() => setActiveMarker(null)}
                        options={{ pixelOffset: new window.google.maps.Size(0, -40) }}
                    >
                        <div style={infoWindowStyles.container}>
                            <h3 style={infoWindowStyles.name}>{activeMarker.name}</h3>
                            <p style={infoWindowStyles.tagline}>{activeMarker.tagline}</p>
                            <div style={infoWindowStyles.status}>
                                {checkedInVenueIds.includes(activeMarker.id) ? '🏅 Checked In' : '📍 Not Visited'}
                            </div>
                            <button
                                style={infoWindowStyles.btn}
                                onClick={() => {
                                    setActiveMarker(null);
                                    onVenueSelect(activeMarker);
                                }}
                            >
                                View Details →
                            </button>
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
        </div>
    );
}

const legendStyles = {
    container: {
        position: 'absolute', top: 16, left: 16, zIndex: 10,
        background: 'rgba(255,255,255,0.95)', padding: '12px 16px',
        borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        fontFamily: 'DM Sans, sans-serif', fontSize: 13
    },
    title: { fontWeight: 'bold', marginBottom: 8, color: '#0F1A1F', fontFamily: 'Playfair Display, serif' },
    item: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: '#7A8F96', fontWeight: 500 },
    swatch: { width: 14, height: 14, borderRadius: '50%' }
};

const infoWindowStyles = {
    container: { padding: 8, minWidth: 200, fontFamily: 'DM Sans, sans-serif', textAlign: 'center' },
    name: { margin: '0 0 4px 0', fontSize: 16, color: '#0F1A1F', fontFamily: 'Playfair Display, serif' },
    tagline: { margin: '0 0 8px 0', fontSize: 12, color: '#7A8F96', fontStyle: 'italic' },
    status: { fontSize: 12, fontWeight: 'bold', color: '#B87333', marginBottom: 12 },
    btn: { background: '#B87333', color: '#fff', border: 'none', borderRadius: 20, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 'bold', fontFamily: 'DM Sans, sans-serif' }
};

const mapStyles = [
    { featureType: 'administrative', elementType: 'all', stylers: [{ saturation: -100 }] },
    // ... more styles
];

export default CopperRoadMap;
